import constate from "constate";
import { EventCategory } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import { LogLevel } from "logging/datadogBrowserLogs";
import { Dispatch, SetStateAction, useState } from "react";
import { devLog } from "shared/utils/devLog";
import { ZodSchema, ZodTypeDef } from "zod";

export type ErrorResult = string[]; // string[] is just a list of fallback info. e.g. [title, date]
type ErrorResults = { [key in EventCategory]?: ErrorResult[] };

/**
 * The typing is an array of events as Output (and as Input) in order to make `useParseResult` as plug in place as possible.
 * This is because it is common to write like: `get<InfrastructureResponse[]>(...).then((res) => schema.parse(res.data))`, thus res.data is an array.
 * Like this, you could just rewrite `res => schema.parse(res.data)` to `res => parse(schema, res.data)` and useParseResult is easily integrated.
 */
type Schema<Response> = ZodSchema<
  Response[], // Output is an array of events (Response is an event, e.g. InfrastructureResponse)
  ZodTypeDef,
  unknown // Input's type is unknown by default for every zod schema. If typed it would practically be AxiosResponse<Response>[]
>;

function parse<Response>(
  messageFallback: (data: Response) => ErrorResult,
  eventCategory: EventCategory,
  schema: Schema<Response>,
  data: Response[],
  setErrorResults: Dispatch<SetStateAction<ErrorResults>>,
): Response[] {
  const errors: ErrorResult[] = [];
  // We parse one event at a time instead, so that we can keep the successfull parsed ones
  const parsedData = data
    .map((it) => {
      const output = schema.safeParse([it]);
      if (output.success) return output.data[0];
      devLog(LogLevel.error, output.error);
      const error = messageFallback(it);
      errors.push(error);
      return null as Response; // Is filtered anyway
    })
    .filter(Boolean);
  setErrorResults((prev) => ({ ...prev, [eventCategory]: errors }));
  return parsedData;
}

/**
 * This provider safeParses each response and saves unsuccessfull parses at `errorResults`
 * The unsuccessfull parses can then be retrieved from `errors` to be displayed elsewhere.
 *
 * It uses some generic typing to make it flexible. To see how you can use this provider, look
 * at `useUpcommingInfrastructureMessages` or `useGlobalInformation`
 */
function useParseResult() {
  const [errorResults, setErrorResults] = useState<ErrorResults>({});

  /*
   * We split it to handleParse and parse in a similiar fashion as handleSubmit and submit.
   * handleParse readies up the parse with necessary data, while parse is made to closely resemble schema.parse
   */
  function handleParse<Response>(
    messageFallback: (data: Response) => ErrorResult,
    eventCategory: EventCategory,
  ) {
    return (schema: Schema<Response>, data: Response[]) =>
      parse(messageFallback, eventCategory, schema, data, setErrorResults);
  }

  const errors = (eventCategory: EventCategory) => errorResults[eventCategory];

  return { handleParse, errors };
}

export const [ParseResultProvider, useParseResultContext] =
  constate(useParseResult);
